import { Button, Card, CardContent, CardHeader, TextField } from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { makeStyles } from '@material-ui/core/styles'
import React, { useState, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	formatNumsToDollars,
	onlyNumbers,
	NON_BREAKING_SPACE,
	setDateToMidday,
	convertDateStringToDate,
	convertDateToISOString
} from '../../../utils'
import { ArrowBackIosOutlined, ArrowForwardIosOutlined } from '@material-ui/icons'
import { api } from '../../../services/api'
import { showError, showSuccess } from '../../../actions/misc/misc.actions'
import moment from 'moment'

const useStyles = makeStyles((theme) => ({
	root: {
		maxWidth: '1245px'
	},
	container: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		gap: '1.5rem',
		'& > div': {
			display: 'flex',
			flexDirection: 'row',
			flex: 1,
			gap: '1.5rem',
			'& > div': {
				flex: 1,
				'& .MuiFilledInput-root': {
					minWidth: '100%'
				},
				'& .MuiTextField-root': {
					minWidth: '100%'
				}
			}
		}
	},
	buttonContainer: {
		display: 'flex',
		gap: '1rem'
	},
	datePicker: {
		margin: 0
	},
	calendarButton: {
		'& svg': {
			fill: theme.palette.primary.main
		}
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	}
}))

const STRINGS = {
	Title: 'Special Day Pass Pricing - Setup',
	StartDate: 'Start Date',
	EndDate: 'End Date',
	Description: 'Description',
	FirstPet: 'First Dog Price',
	SecondPet: 'Second Dog Price',
	SaveDate: 'Save Date',
	Update: 'Update',
	Cancel: 'Cancel'
}

const MESSAGES = {
	Success: 'Special Pricing Added',
	Error: 'Error Adding Special Pricing'
}

const datesAreEqualOrGreaterThan = (start_date, end_date) => {
	const startDate = setDateToMidday(moment(start_date))
	const endDate = setDateToMidday(moment(end_date))

	const dif = startDate.diff(endDate)
	return dif <= 0
}

const SpecialDayPassPricingSetup = ({ setLocalLoading, setSpecialDayPassPrice, specialDayPassPrice }) => {
	const classes = useStyles()
	const dispatch = useDispatch()

	const selectedFacility = useSelector((state) => state.specific.facilities.selected)

	const [mongoDbId, setMongoDbId] = useState(null)
	const [startDate, setStartDate] = useState(null) // iso date as string
	const [endDate, setEndDate] = useState(null) // iso date as string
	const [description, setDescription] = useState('')
	const [dayPassPrice, setDayPassPrice] = useState(0)
	const [additionalPetPrice, setAdditionalPetPrice] = useState(0)
	const [startDateOpen, setStartDateOpen] = useState(false)
	const [endDateOpen, setEndDateOpen] = useState(false)

	useEffect(() => {
		if (specialDayPassPrice) {
			const {
				_id,
				day_pass_price,
				additional_pet_price,
				description,
				end_date,
				start_date
			} = specialDayPassPrice
			setStartDate(start_date)
			setEndDate(end_date)
			setDescription(description)
			setDayPassPrice(day_pass_price)
			setAdditionalPetPrice(additional_pet_price)
			setMongoDbId(_id)
		} else {
			reset()
		}
	}, [specialDayPassPrice])

	const handleSetStartDate = useCallback((date) => {
		let dateToString = setDateToMidday(date)
		dateToString = convertDateToISOString(dateToString)
		setStartDate(dateToString)
	}, [])

	const handleSetEndDate = useCallback((date) => {
		let dateToString = setDateToMidday(date)
		dateToString = convertDateToISOString(dateToString)
		setEndDate(dateToString)
	}, [])

	const handleStartDateVisible = useCallback((visibility) => {
		setStartDateOpen(visibility)
	}, [])

	const handleEndDateVisible = useCallback((visibility) => {
		setEndDateOpen(visibility)
	}, [])

	const handleSetDescription = useCallback((e) => {
		const { target } = e
		setDescription(target.value)
	}, [])

	const handleSetDayPassPrice = useCallback((e) => {
		const {
			target: { value }
		} = e
		setDayPassPrice(parseInt(onlyNumbers(value)))
	}, [])

	const handleSetAdditionalPetPrice = useCallback((e) => {
		const {
			target: { value }
		} = e
		setAdditionalPetPrice(parseInt(onlyNumbers(value)))
	}, [])

	const getAnyOverlappingDates = useCallback(() => {
		if (selectedFacility) {
			/**
			 *
			 * @param {object[]} proposed
			 * @param {moment} proposed.start moment version of proposed start date
			 * @param {moment} proposed.end moment version of proposed end date
			 * @param {object[]} existing
			 * @param {moment} existing.start moment version of existing start date
			 * @param {moment} existing.end moment verison of existing end date
			 * @returns boolean
			 */
			const testDates = ([proposedStart, proposedEnd], [existingStart, existingEnd]) => {
				const _proposedStart = setDateToMidday(proposedStart)
				const _proposedEnd = setDateToMidday(proposedEnd)
				const _existingStart = setDateToMidday(existingStart)
				const _existingEnd = setDateToMidday(existingEnd)

				const proposedStartEqualsStart = moment(_proposedStart).isSame(_existingStart)
				const proposedStartEqualsEnd = moment(_proposedStart).isSame(_existingEnd)
				const proposedEndEqualsStart = moment(_proposedEnd).isSame(_existingStart)
				const proposedEndEqualsEnd = moment(_proposedEnd).isSame(_existingEnd)
				const propsedStartIsBetweenExisting = moment(_proposedStart).isBetween(
					_existingStart,
					_existingEnd
				)
				const proposedEndIsBetweenExisting = moment(_proposedEnd).isBetween(_existingStart, _existingEnd)
				const existingStartIsBetweenProposed = moment(_existingStart).isBetween(
					_proposedStart,
					_proposedEnd
				)
				const existingEndIsBetweenProposed = moment(_existingEnd).isBetween(_proposedStart, _proposedEnd)

				return (
					proposedStartEqualsStart ||
					proposedStartEqualsEnd ||
					proposedEndEqualsStart ||
					proposedEndEqualsEnd ||
					propsedStartIsBetweenExisting ||
					proposedEndIsBetweenExisting ||
					existingStartIsBetweenProposed ||
					existingEndIsBetweenProposed
				)
			}

			const { special_daypass_prices } = selectedFacility
			const overlap = special_daypass_prices.find(({ _id, start_date, end_date }) => {
				// don't test dates if an existing entry is being updated
				if (_id === mongoDbId) {
					return false
				}
				const proposedDates = [moment(startDate), moment(endDate)]
				const existingDates = [moment(start_date), moment(end_date)]

				return testDates(proposedDates, existingDates)
			})

			return overlap
		}
		return null
	}, [selectedFacility, startDate, endDate, mongoDbId])

	const handleSave = useCallback(() => {
		const apiPayload = {
			start_date: convertDateStringToDate(startDate), // send date as Date object to API
			end_date: convertDateStringToDate(endDate), // send date as Date object to API
			description,
			day_pass_price: dayPassPrice,
			additional_pet_price: additionalPetPrice
		}

		const create = async () => {
			try {
				setLocalLoading(true)
				const { data } = await api.patch(
					`/facilities/specialDayPassPrice/${selectedFacility._id}/create`,
					apiPayload
				)

				if (data.errors.length > 0) {
					dispatch(showError(data.errors[0]))
					return
				}

				apiPayload['_id'] = data.data[0]

				dispatch(showSuccess(data.messages[0]))
				dispatch({
					type: 'UPDATE_FACILITY_SUCCESS',
					payload: {
						...selectedFacility,
						special_daypass_prices: [...selectedFacility.special_daypass_prices, apiPayload]
					}
				})
				reset()
			} catch (e) {
				dispatch(showError(MESSAGES.Error))
				console.log('big ol e...', e)
			} finally {
				setLocalLoading(false)
			}
		}

		const update = async () => {
			try {
				setLocalLoading(true)
				const updatePayload = {
					_id: mongoDbId,
					...apiPayload
				}
				const { data } = await api.patch(
					`/facilities/specialDayPassPrice/${selectedFacility._id}/update`,
					updatePayload
				)

				if (data.errors.length > 0) {
					dispatch(showError(data.errors[0]))
					return
				}

				const existingRecordIndex = selectedFacility.special_daypass_prices.findIndex(
					({ _id }) => _id === mongoDbId
				)
				const newRecords = [...selectedFacility.special_daypass_prices]
				newRecords[existingRecordIndex] = updatePayload

				dispatch(showSuccess(data.messages[0]))
				dispatch({
					type: 'UPDATE_FACILITY_SUCCESS',
					payload: {
						...selectedFacility,
						special_daypass_prices: newRecords
					}
				})
				reset()
			} catch (e) {
				dispatch(showError(MESSAGES.Error))
				console.log('big ol e...', e)
			} finally {
				setLocalLoading(false)
			}
		}

		// If editing an existing and the dates have not changed
		// no need to look for an overlap, can just update the
		// record in the db. If there is no mongoDbId, then its a new record
		if (
			specialDayPassPrice &&
			mongoDbId &&
			startDate === specialDayPassPrice.start_date &&
			endDate === specialDayPassPrice.end_date
		) {
			update()
			return
		}

		const overlap = getAnyOverlappingDates()
		if (overlap) {
			const { description } = overlap
			dispatch(showError(`Overlaps with ${description}`))
			return
		}

		if (mongoDbId) {
			update()
		} else {
			create()
		}
	}, [
		startDate,
		endDate,
		description,
		dayPassPrice,
		additionalPetPrice,
		selectedFacility,
		mongoDbId,
		specialDayPassPrice
	])

	const reset = useCallback(() => {
		setStartDate(null)
		setEndDate(null)
		setDescription('')
		setDayPassPrice(0)
		setAdditionalPetPrice(0)
		setMongoDbId(null)
		setSpecialDayPassPrice(null)
	}, [])

	return (
		<Card className={classes.root}>
			<CardHeader title={STRINGS.Title} />
			<CardContent>
				<div className={classes.container}>
					<div>
						<div>
							<KeyboardDatePicker
								disableToolbar
								required
								open={startDateOpen}
								onOpen={() => handleStartDateVisible(true)}
								onClose={() => handleStartDateVisible(false)}
								variant="inline"
								format="MM/DD/YYYY"
								margin="normal"
								label={STRINGS.StartDate}
								inputVariant="filled"
								value={startDate}
								onChange={handleSetStartDate}
								className={classes.datePicker}
								rightArrowIcon={
									<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />
								}
								leftArrowIcon={<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />}
								KeyboardButtonProps={{
									'aria-label': STRINGS.StartDate,
									className: classes.calendarButton
								}}
							/>
						</div>
						<div>
							<KeyboardDatePicker
								disableToolbar
								required
								open={endDateOpen}
								onOpen={() => handleEndDateVisible(true)}
								onClose={() => handleEndDateVisible(false)}
								variant="inline"
								format="MM/DD/YYYY"
								margin="normal"
								label={STRINGS.EndDate}
								inputVariant="filled"
								value={endDate}
								onChange={(date) => handleSetEndDate(date)}
								className={classes.datePicker}
								rightArrowIcon={
									<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />
								}
								leftArrowIcon={<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />}
								KeyboardButtonProps={{
									'aria-label': STRINGS.EndDate,
									className: classes.calendarButton
								}}
							/>
						</div>
						<div>
							<TextField
								required
								variant="filled"
								label={STRINGS.Description}
								value={description}
								onChange={handleSetDescription}
							/>
						</div>
					</div>
					<div>
						<div>
							<TextField
								variant="filled"
								label={STRINGS.FirstPet}
								required={true}
								onChange={handleSetDayPassPrice}
								value={formatNumsToDollars(onlyNumbers(dayPassPrice))}
							/>
						</div>
						<div>
							<TextField
								variant="filled"
								label={STRINGS.SecondPet}
								required={true}
								value={formatNumsToDollars(onlyNumbers(additionalPetPrice))}
								onChange={handleSetAdditionalPetPrice}
							/>
						</div>
						<div>{NON_BREAKING_SPACE}</div>
					</div>
					<div>
						<div className={classes.buttonContainer}>
							<Button
								variant="contained"
								color="secondary"
								onClick={handleSave}
								disabled={
									!startDate ||
									!endDate ||
									description === '' ||
									dayPassPrice < 0 ||
									additionalPetPrice < 0 ||
									!selectedFacility ||
									!datesAreEqualOrGreaterThan(startDate, endDate)
								}>
								{mongoDbId ? STRINGS.Update : STRINGS.SaveDate}
							</Button>
							<Button variant="outlined" color="secondary" onClick={reset}>
								{STRINGS.Cancel}
							</Button>
						</div>
						<div>{NON_BREAKING_SPACE}</div>
					</div>
				</div>
			</CardContent>
		</Card>
	)
}

export default SpecialDayPassPricingSetup
